// docs/64 VP2: the people of a village on the move. Each figure (a hero, a villager, a passer-by,
// a hen) has a place in cells; a walk moves it along a line at its own pace, and every frame the
// figure's element is written straight to (left, top, z order, which way it faces, whether it is
// walking) with no React render. React draws figures where `at(key)` says they are, so a render
// mid-walk never puts anyone back.
import { useCallback, useEffect, useRef } from 'react';
import { motionReduced } from '../../components';
import { lengthOf, pointAt, type Pt } from './walk';

export interface Place extends Pt { face: 1 | -1; seated?: boolean }
/** How a walk ends: which way the figure then faces, and whether it sits (docs/120 VM2: told to the other views). */
export interface WalkEnd { face?: 1 | -1; seated?: boolean }
interface Walk { line: Pt[]; s: number; to: number; speed: number; done?: () => void; end?: WalkEnd }
/** docs/120 VM2: where a figure is now, as the other views are told: the rest of its walk (from where it is), or where it stands. */
export interface Plan { line: Pt[]; to: number; speed: number; face: 1 | -1; seated: boolean }

export interface Motion {
  /** Where a figure is now (undefined until placed). */
  at(key: string): Place | undefined;
  /** Put a figure somewhere at once (a new room, a seat), stopping any walk. */
  place(key: string, p: Pt, face?: 1 | -1, seated?: boolean): void;
  /** Walk a figure along `line` from `from` to `to` (cells along it) at `speed` cells a second. */
  walk(key: string, line: Pt[], opts?: { from?: number; to?: number; speed?: number; done?: () => void; end?: WalkEnd }): void;
  /** Stop a figure where it is (its `done` is not called). */
  stop(key: string): void;
  /** Turn a figure to face left (−1) or right (1). */
  face(key: string, face: 1 | -1): void;
  walking(key: string): boolean;
  /** Forget a figure (it has left the scene). */
  drop(key: string): void;
  /** A ref callback that hands the figure's element to the engine. */
  reg(key: string): (el: HTMLElement | null) => void;
  /** Every figure placed now (docs/116: who stands where in the inn). */
  keys(): string[];
  /** Called each frame with dt (s) and the keys walking (sounds, idle wanders). */
  onFrame(fn: ((dt: number, walking: string[]) => void) | undefined): void;
  /** docs/120 VM2: the rest of a figure's walk from where it is (or where it stands). */
  plan(key: string): Plan | undefined;
  /** docs/120 VM2: called with a figure's key whenever it is placed, sets off, stops, turns or arrives. */
  onChange(fn: ((key: string) => void) | undefined): void;
}

/** The part of `line` from `s` to `to` cells along it (the figure's place first). */
export function sliceLine(line: readonly Pt[], s: number, to: number): Pt[] {
  const a = pointAt(line, s);
  const out: Pt[] = [{ x: a.x, y: a.y }];
  let d = 0;
  for (let i = 1; i < line.length; i++) {
    d += Math.hypot(line[i]!.x - line[i - 1]!.x, line[i]!.y - line[i - 1]!.y);
    if (d > s + 1e-6 && d < to - 1e-6) out.push({ x: line[i]!.x, y: line[i]!.y });
  }
  const b = pointAt(line, to);
  if (Math.hypot(b.x - out[out.length - 1]!.x, b.y - out[out.length - 1]!.y) > 1e-6) out.push({ x: b.x, y: b.y });
  return out;
}

/** `cellPx` is read every frame (a resize changes it). */
export function useMotion(cellPx: number): Motion {
  const places = useRef(new Map<string, Place>());
  const walks = useRef(new Map<string, Walk>());
  const els = useRef(new Map<string, HTMLElement>());
  const regs = useRef(new Map<string, (el: HTMLElement | null) => void>());
  const cell = useRef(cellPx);
  const tick = useRef<((dt: number, walking: string[]) => void) | undefined>(undefined);
  const changed = useRef<((key: string) => void) | undefined>(undefined);
  const tell = (key: string) => changed.current?.(key);
  cell.current = cellPx;

  const write = useCallback((key: string) => {
    const el = els.current.get(key), p = places.current.get(key);
    if (!el || !p) return;
    const c = cell.current;
    el.style.left = `${Math.round(p.x * c)}px`;
    el.style.top = `${Math.round(p.y * c)}px`;
    el.style.zIndex = String(Math.round(p.y * 10) + 1);
    el.dataset.face = p.face < 0 ? 'left' : 'right';
    if (walks.current.has(key)) el.dataset.walking = 'true'; else delete el.dataset.walking;
    if (p.seated) el.dataset.seated = 'true'; else delete el.dataset.seated;
  }, []);

  useEffect(() => {
    let raf = 0, last = performance.now();
    const frame = (now: number) => {
      raf = requestAnimationFrame(frame);
      const dt = Math.min(0.1, Math.max(0, (now - last) / 1000));
      last = now;
      const moving: string[] = [];
      for (const [key, w] of [...walks.current]) {
        const total = Math.min(w.to, lengthOf(w.line));
        w.s = motionReduced() ? total : Math.min(total, w.s + w.speed * dt);
        const p = pointAt(w.line, w.s);
        const prev = places.current.get(key);
        places.current.set(key, { x: p.x, y: p.y, face: p.dir ? (p.dir as 1 | -1) : prev?.face ?? 1 });
        if (w.s >= total - 1e-4) {
          walks.current.delete(key);
          if (w.end) { const q = places.current.get(key)!; places.current.set(key, { x: q.x, y: q.y, face: w.end.face ?? q.face, ...(w.end.seated ? { seated: true } : {}) }); }
          write(key);
          w.done?.();
          tell(key);
        } else { moving.push(key); write(key); }
      }
      tick.current?.(dt, moving);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [write]);

  // a resize (another scale) moves everyone to their place on the new cells
  useEffect(() => { for (const k of places.current.keys()) write(k); }, [cellPx, write]);

  return useRef<Motion>({
    at: (key) => places.current.get(key),
    place: (key, p, face, seated) => {
      walks.current.delete(key);
      places.current.set(key, { x: p.x, y: p.y, face: face ?? places.current.get(key)?.face ?? 1, ...(seated ? { seated } : {}) });
      write(key);
      tell(key);
    },
    walk: (key, line, opts = {}) => {
      if (line.length < 2) { opts.done?.(); return; }
      const cur = places.current.get(key);
      places.current.set(key, { x: cur?.x ?? line[0]!.x, y: cur?.y ?? line[0]!.y, face: cur?.face ?? 1 });
      walks.current.set(key, { line, s: opts.from ?? 0, to: opts.to ?? Infinity, speed: opts.speed ?? 3, ...(opts.done ? { done: opts.done } : {}), ...(opts.end ? { end: opts.end } : {}) });
      tell(key);
    },
    stop: (key) => { if (walks.current.delete(key)) { write(key); tell(key); } },
    face: (key, face) => { const p = places.current.get(key); if (p && p.face !== face) { places.current.set(key, { ...p, face }); write(key); tell(key); } },
    walking: (key) => walks.current.has(key),
    drop: (key) => { walks.current.delete(key); places.current.delete(key); },
    reg: (key) => {
      let r = regs.current.get(key);
      if (!r) {
        r = (el) => { if (el) { els.current.set(key, el); write(key); } else els.current.delete(key); };
        regs.current.set(key, r);
      }
      return r;
    },
    keys: () => [...places.current.keys()],
    onFrame: (fn) => { tick.current = fn; },
    plan: (key) => {
      const p = places.current.get(key);
      if (!p) return undefined;
      const w = walks.current.get(key);
      if (!w) return { line: [{ x: p.x, y: p.y }], to: 0, speed: 0, face: p.face, seated: !!p.seated };
      const total = Math.min(w.to, lengthOf(w.line));
      const line = sliceLine(w.line, w.s, total);
      const last = pointAt(w.line, total);
      return { line, to: lengthOf(line), speed: w.speed, face: w.end?.face ?? (last.dir ? (last.dir as 1 | -1) : p.face), seated: !!w.end?.seated };
    },
    onChange: (fn) => { changed.current = fn; },
  }).current;
}
